import type { CommandManager } from '../../../core/commands/CommandManager';
import { CompositeCommand } from '../../../core/commands/ICommand';
import {
  CreateNetworkResourceCommand,
  CreateNetworkConnectorCommand,
} from '../commands';
import type { ImportResult, ImportedResource, ImportedEdge } from '../../../lib/cloud-importers';
import { NetworkLayoutService } from '../services/NetworkLayoutService';
import { getShapeManifest } from '../../../lib/diagram/registries';
import {
  CONTAINER_PROVIDER_SHAPE_IDS,
  getProviderShapeSpec,
} from '../providers/providerDialectRegistry';
import type * as Y from 'yjs';

/**
 * Hydration pipeline — takes an ImportResult from any cloud importer
 * and turns it into a single undoable batch of CreateShapeCommands +
 * CreateEdgeCommands.
 *
 * Layout: runs `NetworkLayoutService` before building commands so the
 * scene lands in an ELK-routed (orthogonal, hierarchy-aware) layout
 * instead of a flat grid. Falls back to the grid for any node ELK
 * can't position.
 *
 * Connectors: every non-containment edge is tagged with
 * `routingType: 'orthogonal'` so EdgeConnector renders right-angle
 * paths instead of bezier curves. Containment edges are not turned
 * into canvas connectors — they are structural and become Frame
 * nesting via the doc's `frames` Y.Map (see Pass 3 below).
 *
 * Frames: VPC/Subnet/Region/Account containers are NOT created as
 * canvas shapes. Instead we walk the containment hierarchy bottom-up
 * and write frame records directly to the doc, with bounds computed
 * from the layouted positions of each container's transitive
 * children. `DiagramCanvasWithTransform` already syncs that Y.Map
 * into a live `FrameManager`, so the canvas picks them up without
 * any wiring changes.
 *
 * Resource ids from the importer are opaque strings (ARNs, logical ids,
 * CSV-authored keys). Canvas node ids must be globally unique per
 * document, so we generate fresh ids and keep a map from importer-id
 * → canvas-id so edges resolve to the right targets.
 *
 * Undo boundary: the entire import lands as a single CompositeCommand
 * pushed through CommandManager — one click of Undo reverses the whole
 * batch. The Frame writes that bypass commands (the direct Y.Map
 * mutation described above) are intentionally outside that undo scope:
 * frames are derived view state computed from container positions, and
 * the FrameManager rebuilds them on the next layout pass after the
 * shapes are removed. This is the only direct Y mutation in this file;
 * everything that affects diagram-model state still goes through
 * CommandManager.
 */

interface HydrateOptions {
  /** Top-left of the grid in canvas coordinates. */
  origin?: { x: number; y: number };
  /** Cell stride — tuned for the default network shape dimensions. */
  cell?: { width: number; height: number };
  /** Columns per row before wrapping. */
  columns?: number;
  /** Command name shown in the undo stack. */
  commandName?: string;
  /**
   * Yjs document for the diagram. Required when you want container
   * resources (VPC/Subnet/etc.) to render as Frames. When omitted,
   * containers are hidden entirely and only their leaf children land
   * on the canvas.
   */
  doc?: Y.Doc;
  /**
   * When set, only hydrate resources whose importer-id is in this set.
   * Edges are filtered to those where both endpoints pass. Used by
   * the view materializer to render a query-driven subset.
   */
  subsetResourceIds?: Set<string>;
  /**
   * Optional plan shape cap for batch materialization. Counts every
   * generated canvas node/connector command against existing nodes+edges.
   * When exceeded, hydration returns a blocked report before mutating the
   * CommandManager or frame map.
   */
  shapeLimit?: {
    currentCount: number;
    maxShapes: number | null;
    planType?: string;
  };
}

export interface HydrationReport {
  createdResourceIds: string[];
  createdEdgeIds: string[];
  createdFrameIds: string[];
  skippedResources: number;
  skippedEdges: number;
  warnings: string[];
  shapeLimitBlocked?: boolean;
  shapeLimit?: {
    current: number;
    limit: number;
    attemptedAdditionalShapes: number;
    planType?: string;
  };
}

const DEFAULT_ORIGIN = { x: 200, y: 200 };
const DEFAULT_CELL = { width: 220, height: 120 };
const DEFAULT_COLUMNS = 6;

const CONTAINER_MANIFEST_IDS = new Set<string>([
  'aws_account',
  'aws_region',
  'aws_vpc',
  'aws_subnet',
  'aws_subnet_public',
  'aws_subnet_private',
  ...CONTAINER_PROVIDER_SHAPE_IDS,
]);

const FRAME_PADDING = 24;
const FRAME_TITLE_HEIGHT = 32;

type EdgePresentation = {
  visible: boolean;
  edgeTier: 'data' | 'control';
  color: string;
  width: number;
  opacity: number;
  dashed?: boolean;
  dash?: number[];
  label?: string;
  endArrow: { type: string; size: number };
};

export async function hydrateImportedResources(
  result: ImportResult,
  commandManager: CommandManager,
  options: HydrateOptions = {},
): Promise<HydrationReport> {
  const origin = options.origin ?? DEFAULT_ORIGIN;
  const cell = options.cell ?? DEFAULT_CELL;
  const columns = options.columns ?? DEFAULT_COLUMNS;
  const commandName = options.commandName ?? `Import ${result.sourceLabel}`;

  const report: HydrationReport = {
    createdResourceIds: [],
    createdEdgeIds: [],
    createdFrameIds: [],
    skippedResources: 0,
    skippedEdges: 0,
    warnings: [...result.warnings],
  };

  if (result.resources.length === 0) {
    report.warnings.push('Nothing to import — resource list is empty');
    return report;
  }

  // ───── Subset filtering (view materialization) ─────
  if (options.subsetResourceIds) {
    const subset = options.subsetResourceIds;
    result = {
      ...result,
      resources: result.resources.filter((r) => subset.has(r.id)),
      edges: result.edges.filter((e) => subset.has(e.from) && subset.has(e.to)),
    };
    if (result.resources.length === 0) {
      report.warnings.push('Subset filter matched no resources');
      return report;
    }
  }

  const importIdToCanvasId = new Map<string, string>();

  // Container manifests are not laid out as canvas shapes — they
  // become Frames in Pass 3. Track them separately so they keep
  // canvas ids (used as frame ids) but don't generate shape commands.
  const containerResources = result.resources.filter((r) => CONTAINER_MANIFEST_IDS.has(r.manifestId));
  const leafResources = result.resources.filter((r) => !CONTAINER_MANIFEST_IDS.has(r.manifestId));

  // Container-first ordering on the leaves is preserved so the grid
  // fallback stays deterministic. Containers themselves don't appear
  // on the canvas so their order doesn't matter beyond id reservation.
  const containerFirst = leafResources;

  // Reserve canvas ids before we ever talk to the layout service so that
  // node refs and edge refs use the same id space.
  const reservedAt = Date.now().toString(36);
  containerResources.forEach((resource, index) => {
    const canvasId = `net-frame-${safeSlug(resource.id)}-${reservedAt}-${index}`;
    importIdToCanvasId.set(resource.id, canvasId);
  });
  containerFirst.forEach((resource, index) => {
    const canvasId = `net-${safeSlug(resource.id)}-${reservedAt}-${index}`;
    importIdToCanvasId.set(resource.id, canvasId);
  });

  // Resolve display dimensions for every leaf resource from its manifest, so
  // ELK lays out shapes at their real size and the create command uses the
  // same numbers.
  const dimsByCanvasId = new Map<string, { width: number; height: number }>();
  containerFirst.forEach((resource) => {
    const canvasId = importIdToCanvasId.get(resource.id)!;
    const manifest = getShapeManifest('network', resource.manifestId);
    const fallback = manifest?.defaultDimensions ?? { width: 180, height: 60 };
    dimsByCanvasId.set(canvasId, { width: fallback.width, height: fallback.height });
  });

  // ───── Run ELK layout up front ─────
  // We only feed leaf nodes + non-containment edges to the layout
  // service. Containers will wrap their layouted children as Frames
  // in Pass 3. ELK's INCLUDE_CHILDREN hierarchy mode crashes on
  // real-world infra graphs (multi-parent children, dense
  // cross-hierarchy edges) and even when it doesn't, the canvas
  // already renders containers via the Frame system, not nested
  // ELK clusters — so flat layout of the dependency graph is the
  // correct input here.
  const layoutNodes = containerFirst.map((resource) => {
    const canvasId = importIdToCanvasId.get(resource.id)!;
    const dims = dimsByCanvasId.get(canvasId)!;
    return { id: canvasId, type: resource.manifestId, width: dims.width, height: dims.height };
  });
  const layoutEdges = dedupeImportedEdges(result.edges)
    .filter((edge) => shouldAffectDefaultImportLayout(edge))
    .map((edge) => {
      const from = importIdToCanvasId.get(edge.from);
      const to = importIdToCanvasId.get(edge.to);
      // Containers are not in the layout graph, so any edge that
      // touches one is silently dropped from layout (it still
      // becomes a canvas connector below if both endpoints are leaves).
      if (!from || !to) return null;
      // Skip edges that hit a container — there's no node for ELK to
      // route to. We keep them in the source list for canvas creation
      // because they become frame-edge crossings rendered by
      // EdgeConnector when both endpoints land on actual nodes.
      if (!dimsByCanvasId.has(from) || !dimsByCanvasId.has(to)) return null;
      return {
        id: `layout-${pickEdgeType(edge)}-${from}-${to}`,
        from,
        to,
        type: pickEdgeType(edge),
      };
    })
    .filter((e): e is { id: string; from: string; to: string; type: string } => e !== null);

  let layoutPositions = new Map<string, { x: number; y: number }>();
  try {
    const layoutService = new NetworkLayoutService();
    const layoutResult = await layoutService.applyLayout(layoutNodes, layoutEdges, {
      strategy: 'down-tree',
      nodeSpacing: 60,
      rankSpacing: 100,
    });
    layoutPositions = layoutResult.nodePositions;
  } catch (err) {
    report.warnings.push(`Auto-layout failed, falling back to grid: ${(err as Error)?.message ?? err}`);
  }

  const commands: Array<CreateNetworkResourceCommand | CreateNetworkConnectorCommand> = [];

  // ───── Pass 1: lay out every resource as a CreateShapeCommand ─────
  containerFirst.forEach((resource, index) => {
    const canvasId = importIdToCanvasId.get(resource.id)!;
    const dims = dimsByCanvasId.get(canvasId)!;

    const layouted = layoutPositions.get(canvasId);
    let x: number;
    let y: number;
    if (layouted) {
      x = layouted.x + origin.x;
      y = layouted.y + origin.y;
    } else {
      const col = index % columns;
      const row = Math.floor(index / columns);
      x = origin.x + col * cell.width;
      y = origin.y + row * cell.height;
    }

    const attrs: Record<string, unknown> = {
      ...(resource.attributes ?? {}),
      resourceId: resource.id,
      // Stamp the manifest id explicitly so the round-trip
      // `sceneToImportResult` → `iacDiff` survives a future rename of the
      // implicit `node.type === manifestId` convention. Backward-compat:
      // `compassGraphToImportResult` already reads attrs._manifestId with
      // fallback to node.type.
      _manifestId: resource.manifestId,
      name: resource.name,
      region: resource.region,
      accountId: resource.accountId,
      az: resource.az,
      tags: resource.tags ?? {},
      hostingProvider: resource.hostingProvider,
      runtimeProvider: resource.runtimeProvider,
      dialectId: resource.dialectId,
      providerLocalType: resource.providerLocalType,
      importedFrom: result.format,
    };

    commands.push(
      new CreateNetworkResourceCommand({
        id: canvasId,
        type: resource.manifestId,
        x,
        y,
        width: dims.width,
        height: dims.height,
        text: resource.name,
        attrs,
      }),
    );
    report.createdResourceIds.push(canvasId);
  });

  // ───── Pass 2: edges ─────
  // Containment edges are structural and will be expressed as Frame
  // nesting in Pass 3 — we drop them from the canvas to remove the
  // tangle of parent→child lines that otherwise dominate the import
  // view. Edges that touch a container endpoint (which has no canvas
  // node) are also dropped: those become frame-edge crossings if the
  // user wires them up later.
  //
  // Every remaining edge gets `routingType: 'orthogonal'` so
  // EdgeConnector renders right-angle paths instead of curves. Low-signal
  // control-plane relationships are retained in the diagram data but hidden
  // from the default architecture view; users can still reason over them via
  // network analysis tools because the edge records exist in the store.
  const leafCanvasIds = new Set(Array.from(dimsByCanvasId.keys()));
  const dedupedEdges = dedupeImportedEdges(result.edges);
  for (const edge of dedupedEdges) {
    const from = importIdToCanvasId.get(edge.from);
    const to = importIdToCanvasId.get(edge.to);
    if (!from || !to) {
      report.skippedEdges++;
      continue;
    }
    if (edge.kind === 'containment') {
      report.skippedEdges++;
      continue;
    }
    if (!leafCanvasIds.has(from) || !leafCanvasIds.has(to)) {
      // Edge touches a container — it would dangle in the canvas
      // because the container is a frame, not a node. Drop it.
      report.skippedEdges++;
      continue;
    }
    const edgeType = pickEdgeType(edge);
    const edgeId = `edge-${edgeType}-${from}-${to}`;
    const presentation = edgePresentationFor(edge);
    commands.push(
      new CreateNetworkConnectorCommand({
        id: edgeId,
        type: edgeType,
        from,
        to,
        attrs: {
          ...(edge.attributes ?? {}),
          infraEdgeKind: edge.kind,
          importedFrom: result.format,
          routingType: 'orthogonal',
          networkImportEdge: true,
          edgeTier: presentation.edgeTier,
          visible: presentation.visible,
          color: presentation.color,
          width: presentation.width,
          opacity: presentation.opacity,
          dashed: presentation.dashed,
          dash: presentation.dash,
          label: presentation.label,
          endArrow: presentation.endArrow,
        },
      }),
    );
    report.createdEdgeIds.push(edgeId);
  }

  if (commands.length === 0) {
    report.warnings.push('No commands were generated from the import');
    return report;
  }

  const maxShapes = options.shapeLimit?.maxShapes ?? null;
  if (maxShapes !== null) {
    const currentCount = Math.max(0, options.shapeLimit?.currentCount ?? 0);
    const attemptedAdditionalShapes = commands.length;
    const nextCount = currentCount + attemptedAdditionalShapes;
    if (nextCount > maxShapes) {
      report.shapeLimitBlocked = true;
      report.shapeLimit = {
        current: nextCount,
        limit: maxShapes,
        attemptedAdditionalShapes,
        planType: options.shapeLimit?.planType,
      };
      report.createdResourceIds = [];
      report.createdEdgeIds = [];
      report.createdFrameIds = [];
      report.warnings.push(
        `PLAN_SHAPE_LIMIT_EXCEEDED: importing ${attemptedAdditionalShapes} shape(s) would raise this diagram to ${nextCount}/${maxShapes} shapes.`,
      );
      return report;
    }
  }

  await commandManager.execute(new CompositeCommand(commandName, commands));

  // ───── Pass 3: frames ─────
  // Walk the containment hierarchy bottom-up so each frame's bounds
  // can encompass either its direct leaf children or already-built
  // child frames. Frames are written straight to the doc's `frames`
  // Y.Map; DiagramCanvasWithTransform syncs that into FrameManager
  // and FrameRenderer picks them up on the next render.
  if (options.doc && containerResources.length > 0) {
    try {
      const framesAdded = buildContainerFrames({
        doc: options.doc,
        containerResources,
        result,
        importIdToCanvasId,
        layoutPositions,
        dimsByCanvasId,
        origin,
        warnings: report.warnings,
      });
      report.createdFrameIds.push(...framesAdded);
    } catch (err) {
      report.warnings.push(`Frame creation failed: ${(err as Error)?.message ?? err}`);
    }
  } else if (containerResources.length > 0) {
    report.warnings.push(
      `Skipped ${containerResources.length} container resource(s) — pass options.doc to render them as frames`,
    );
    report.skippedResources += containerResources.length;
  }

  return report;
}

// ───────────── helpers ─────────────

function pickEdgeType(edge: ImportedEdge): string {
  switch (edge.kind) {
    case 'containment':
      return 'containment';
    case 'sg_access':
      return 'sg_access';
    case 'identity':
      return 'role_assumption';
    case 'routing':
      return 'routing';
    case 'governance':
      return 'governance';
    case 'data_flow':
    default:
      return 'data_flow';
  }
}

function shouldAffectDefaultImportLayout(edge: ImportedEdge): boolean {
  if (edge.kind === 'containment') return false;
  // Data-plane edges should shape the visible architecture. Security,
  // IAM, and governance edges are overlays; feeding them into ELK tends
  // to pull unrelated subnets together and produce long canvas-spanning
  // lines in the default view.
  return edge.kind === 'data_flow' || edge.kind === 'routing';
}

function dedupeImportedEdges(edges: ImportedEdge[]): ImportedEdge[] {
  const byPresentationKey = new Map<string, ImportedEdge & { attributes?: Record<string, unknown> }>();
  for (const edge of edges) {
    const key = `${edge.kind}:${edge.from}->${edge.to}`;
    const existing = byPresentationKey.get(key);
    if (!existing) {
      byPresentationKey.set(key, {
        ...edge,
        attributes: { ...(edge.attributes ?? {}) },
      });
      continue;
    }
    existing.attributes = {
      ...(existing.attributes ?? {}),
      repeatedRelationshipCount: Number(existing.attributes?.repeatedRelationshipCount ?? 1) + 1,
      repeatedRelationshipIds: [
        ...asStringArray(existing.attributes?.repeatedRelationshipIds),
        edge.id,
      ],
    };
  }
  return Array.from(byPresentationKey.values());
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function edgePresentationFor(edge: ImportedEdge): EdgePresentation {
  switch (edge.kind) {
    case 'routing':
      return {
        visible: true,
        edgeTier: 'data',
        color: '#15803d',
        width: 2,
        opacity: 0.86,
        label: 'route',
        endArrow: { type: 'chevron', size: 9 },
      };
    case 'data_flow':
      return {
        visible: true,
        edgeTier: 'data',
        color: '#2563eb',
        width: 2.25,
        opacity: 0.9,
        label: 'data',
        endArrow: { type: 'chevron', size: 9 },
      };
    case 'sg_access':
      return {
        visible: true,
        edgeTier: 'control',
        color: '#d97706',
        width: 1.25,
        opacity: 0.28,
        dashed: true,
        dash: [8, 6],
        label: 'security',
        endArrow: { type: 'chevron', size: 7 },
      };
    case 'identity':
      return {
        visible: false,
        edgeTier: 'control',
        color: '#7c3aed',
        width: 1.25,
        opacity: 0.22,
        dashed: true,
        dash: [4, 5],
        label: 'identity',
        endArrow: { type: 'chevron', size: 7 },
      };
    case 'governance':
      return {
        visible: false,
        edgeTier: 'control',
        color: '#64748b',
        width: 1.15,
        opacity: 0.2,
        dashed: true,
        dash: [2, 5],
        label: 'governance',
        endArrow: { type: 'chevron', size: 7 },
      };
    case 'containment':
    default:
      return {
        visible: false,
        edgeTier: 'control',
        color: '#94a3b8',
        width: 1,
        opacity: 0.2,
        dashed: true,
        label: 'contains',
        endArrow: { type: 'none', size: 0 },
      };
  }
}

function safeSlug(input: string): string {
  return input.replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 40);
}

interface BuildFramesArgs {
  doc: Y.Doc;
  containerResources: ImportedResource[];
  result: ImportResult;
  importIdToCanvasId: Map<string, string>;
  layoutPositions: Map<string, { x: number; y: number }>;
  dimsByCanvasId: Map<string, { width: number; height: number }>;
  origin: { x: number; y: number };
  warnings: string[];
}

interface FrameBounds { x: number; y: number; width: number; height: number }

function buildContainerFrames(args: BuildFramesArgs): string[] {
  const {
    doc,
    containerResources,
    result,
    importIdToCanvasId,
    layoutPositions,
    dimsByCanvasId,
    origin,
    warnings,
  } = args;

  // Map: container importer-id → list of direct child importer-ids
  // (children may themselves be containers — we'll resolve recursively).
  const directChildren = new Map<string, string[]>();
  // Map: child importer-id → first parent container importer-id
  // (multi-parent children pick the first containment edge — same as
  // NetworkLayoutService's defensive dedup).
  const firstParentOf = new Map<string, string>();

  const containerIdSet = new Set(containerResources.map((r) => r.id));

  for (const edge of result.edges) {
    if (edge.kind !== 'containment') continue;
    if (!containerIdSet.has(edge.from)) continue;
    if (!directChildren.has(edge.from)) directChildren.set(edge.from, []);
    directChildren.get(edge.from)!.push(edge.to);
    if (!firstParentOf.has(edge.to)) {
      firstParentOf.set(edge.to, edge.from);
    }
  }

  // Bottom-up topological order: a container is "ready" when none of
  // its direct children are containers we haven't framed yet.
  const built = new Map<string, FrameBounds>();
  const remaining = new Set(containerResources.map((r) => r.id));
  const orderedContainerIds: string[] = [];
  let safety = containerResources.length + 1;
  while (remaining.size > 0 && safety-- > 0) {
    const ready: string[] = [];
    for (const id of remaining) {
      const children = directChildren.get(id) ?? [];
      const allChildrenReady = children.every(
        (cid) => !containerIdSet.has(cid) || built.has(cid),
      );
      if (allChildrenReady) ready.push(id);
    }
    if (ready.length === 0) {
      // Cycle in containment graph (shouldn't happen for AWS, but be defensive).
      warnings.push(`Containment cycle detected, skipping ${remaining.size} container(s)`);
      break;
    }
    for (const id of ready) {
      const bounds = computeFrameBounds(id, {
        directChildren,
        built,
        importIdToCanvasId,
        layoutPositions,
        dimsByCanvasId,
        origin,
      });
      if (bounds) {
        built.set(id, bounds);
        orderedContainerIds.push(id);
      } else {
        warnings.push(`Container "${id}" has no positioned children — frame skipped`);
      }
      remaining.delete(id);
    }
  }

  // Look up: importer-id → ImportedResource (for name + manifest type).
  const resourceById = new Map(containerResources.map((r) => [r.id, r]));

  // Write to the doc's `frames` Y.Map. DiagramCanvasWithTransform
  // bootstraps from this map on mount (and listens for updates),
  // syncing into FrameManager → FrameRenderer.
  const framesMap = doc.getMap<Record<string, unknown>>('frames');
  const created: string[] = [];
  doc.transact(() => {
    for (const importerId of orderedContainerIds) {
      const resource = resourceById.get(importerId)!;
      const bounds = built.get(importerId)!;
      const frameId = importIdToCanvasId.get(importerId)!;
      const parentImporterId = firstParentOf.get(importerId);
      const parentFrameId = parentImporterId ? importIdToCanvasId.get(parentImporterId) : undefined;

      // Membership sets so FrameManager knows which shapes belong
      // inside (used by spatial queries, drop-targeting, scenario
      // grouping). Walk every transitive child of this container.
      const memberShapeIds = new Set<string>();
      const stack = [...(directChildren.get(importerId) ?? [])];
      while (stack.length > 0) {
        const childId = stack.pop()!;
        if (containerIdSet.has(childId)) {
          // Don't add child container's leaves to OUR shapes — they
          // belong to the child frame's shapes set instead.
          continue;
        }
        const childCanvasId = importIdToCanvasId.get(childId);
        if (childCanvasId && dimsByCanvasId.has(childCanvasId)) {
          memberShapeIds.add(childCanvasId);
        }
      }

      framesMap.set(frameId, {
        id: frameId,
        name: resource.name || resource.manifestId,
        type: 'generic',
        bounds,
        shapes: Array.from(memberShapeIds),
        connectors: [],
        parentFrame: parentFrameId,
        visible: true,
        locked: false,
        borderColor: borderColorForContainer(resource.manifestId),
        borderWidth: 2,
        opacity: 1,
        metadata: {
          source: 'network-import',
          containerType: resource.manifestId,
          resourceId: resource.id,
          region: resource.region,
          accountId: resource.accountId,
          hostingProvider: resource.hostingProvider,
          runtimeProvider: resource.runtimeProvider,
          dialectId: resource.dialectId,
          providerLocalType: resource.providerLocalType,
          tags: resource.tags,
        },
      });
      created.push(frameId);
    }
  });
  return created;
}

function computeFrameBounds(
  containerId: string,
  ctx: {
    directChildren: Map<string, string[]>;
    built: Map<string, FrameBounds>;
    importIdToCanvasId: Map<string, string>;
    layoutPositions: Map<string, { x: number; y: number }>;
    dimsByCanvasId: Map<string, { width: number; height: number }>;
    origin: { x: number; y: number };
  },
): FrameBounds | null {
  const children = ctx.directChildren.get(containerId) ?? [];
  if (children.length === 0) return null;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let included = 0;
  for (const childId of children) {
    if (ctx.built.has(childId)) {
      // Child is itself a container with an already-built frame.
      const b = ctx.built.get(childId)!;
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.width);
      maxY = Math.max(maxY, b.y + b.height);
      included++;
      continue;
    }
    const childCanvasId = ctx.importIdToCanvasId.get(childId);
    if (!childCanvasId) continue;
    const pos = ctx.layoutPositions.get(childCanvasId);
    if (!pos) continue;
    const dims = ctx.dimsByCanvasId.get(childCanvasId);
    if (!dims) continue;
    const x = pos.x + ctx.origin.x;
    const y = pos.y + ctx.origin.y;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + dims.width);
    maxY = Math.max(maxY, y + dims.height);
    included++;
  }
  if (included === 0) return null;
  return {
    x: minX - FRAME_PADDING,
    y: minY - FRAME_PADDING - FRAME_TITLE_HEIGHT,
    width: maxX - minX + FRAME_PADDING * 2,
    height: maxY - minY + FRAME_PADDING * 2 + FRAME_TITLE_HEIGHT,
  };
}

function borderColorForContainer(manifestId: string): string {
  const providerSpec = getProviderShapeSpec(manifestId);
  if (providerSpec) return providerSpec.color;
  switch (manifestId) {
    case 'aws_account': return '#0ea5e9';
    case 'aws_region': return '#a855f7';
    case 'aws_vpc': return '#22c55e';
    case 'aws_subnet': return '#94a3b8';
    default: return '#cbd5e1';
  }
}

/**
 * Convenience helper for contexts that only need the first shape
 * without running the full hydration (e.g. a drag-drop preview).
 */
export function toShapeCommandPreview(resource: ImportedResource, origin = { x: 0, y: 0 }) {
  return new CreateNetworkResourceCommand({
    type: resource.manifestId,
    x: origin.x,
    y: origin.y,
    width: 180,
    height: 80,
    text: resource.name,
    attrs: {
      ...(resource.attributes ?? {}),
      resourceId: resource.id,
      _manifestId: resource.manifestId,
      name: resource.name,
    },
  });
}
